import { defineElement, html, i18n, TemplateElement } from '@webtides/element-js';
import { classMap } from 'lit-html/directives/class-map.js';
import style from './buy-box-spec.css';
import '../../../elements/color-button-toggle/color-button-toggle';
import '../../../../legacy-library/webcomponents/arrow-box/arrow-box';
import CurrencyFormatter from '../../../../js/helper/currency-formatter';
import VariantStore from '../VariantStore';
import SelectedSpecs from '../SelectedSpecs';
import ProductPageEvents from '../../../cms-pages/product-page/ProductPageEvents';

export default class BuyBoxSpec extends TemplateElement {
    constructor() {
        super({ shadowRender: false, styles: [style] });
        this._hoverTimer = setTimeout(() => {}, 0);
    }

    properties() {
        return {
            specName: '',
            selectedSpecs: null,
            spec: null,
            hoveredName: '',
        };
    }

    get activeLabel() {
        const selectedValue = this.getValueByName(this.hoveredName || this.selectedName);
        return selectedValue.hint;
    }

    get isShop() {
        return window.siteConfig().isShop || false;
    }

    get showProductSpecHint() {
        return window.siteConfig().showProductSpecHint || false;
    }

    get selectedName() {
        return this.selectedSpecs.get(this.specName);
    }

    get disableNotAvailableVariants() {
        return window.siteConfig().disableNotAvailableVariants ?? false;
    }

    events() {
        return {
            document: {
                [ProductPageEvents.PRODUCT_COMPLETELY_LOADED]: () => {
                    this.requestUpdate();
                },
            },
            '[data-click]': {
                click: e => {
                    const selectedName = e.currentTarget.dataset.name;
                    const selectedIndex = e.currentTarget.dataset.index;
                    this.dispatch(
                        'spec-changed',
                        { name: this.spec.name, value: selectedName, index: selectedIndex },
                        true
                    );
                },
                mouseover: e => {
                    clearTimeout(this._hoverTimer);
                    this.hoveredName = e.currentTarget.dataset.name;
                },
                mouseout: () => {
                    this._hoverTimer = setTimeout(() => {
                        this.hoveredName = null;
                    }, 100);
                },
            },
        };
    }

    isSelected(name) {
        return this.selectedName.toString() === name.toString();
    }

    getToBeSelectedVariant(value) {
        const toBeSelectedSpecs = this.selectedSpecs.clone();
        toBeSelectedSpecs.set(this.spec.name, value);
        const toBeSelectedSpecsString = toBeSelectedSpecs.toString();
        return VariantStore.getVariantBySpecCombination(toBeSelectedSpecsString);
    }

    isAvailable(value) {
        if (!this.disableNotAvailableVariants) {
            return true;
        }
        // toBeSelectedVariant is undefined
        const toBeSelectedVariant = this.getToBeSelectedVariant(value);
        return toBeSelectedVariant?.available ?? false;
    }

    getPriceDifference(value) {
        const currentlySelectedSpecsString = this.selectedSpecs.toString();

        const currentlySelectedVariant = VariantStore.getVariantBySpecCombination(currentlySelectedSpecsString);
        const toBeSelectedVariant = this.getToBeSelectedVariant(value);

        if (currentlySelectedVariant && toBeSelectedVariant) {
            const priceDifference = this.getPriceDelta(toBeSelectedVariant, currentlySelectedVariant);
            return priceDifference === 0 ? false : priceDifference;
        } else {
            return 0;
        }
    }

    getPriceDelta(toBeSelectedVariant, currentlySelectedVariant) {
        if (!toBeSelectedVariant.prices?.sale || !currentlySelectedVariant.prices?.sale) return 0;
        return toBeSelectedVariant.prices?.sale?.centValue - currentlySelectedVariant.prices?.sale?.centValue;
    }

    getValueByName(name) {
        return this.spec.values[name];
    }

    getTooltipLabelByAvailability(value) {
        const toBeSelectedVariant = this.getToBeSelectedVariant(value);
        if (!toBeSelectedVariant?.availabilities) return '';
        return toBeSelectedVariant?.availabilities[0]?.title ?? `${i18n('UNAVAILABLE')}`;
    }

    template() {
        if (!this.selectedName) return html``;

        // this._selectedSpecs = new SelectedSpecs(this.selectedSpecs);
        return html`
            <div class="flex items-center flex-wrap">
                ${Object.values(this.spec.values).map(
                    (value, index) =>
                        html`
                            <div class="spec-value inline-block mx-1 py-1 relative">
                                ${!this.isAvailable(value.name) ? this.tooltipTemplate(value) : ''}
                                ${this.spec.name === 'FARB'
                                    ? this.colorButtonTemplate(value, index)
                                    : this.buttonTemplate(value, index)}
                            </div>
                        `
                )}
                ${(this.showProductSpecHint || this.spec.name === 'FARB') && this.activeLabel
                    ? html` <div class="active-spec-label w-full text-sm mt-1 mx-1">${this.activeLabel}</div> `
                    : ''}
            </div>
        `;
    }

    buttonTemplate(value, index) {
        const priceDifference = this.getPriceDifference(value.name);
        const btnClasses = {
            'is-active': this.isSelected(value.name),
            'is-disabled': !this.isAvailable(value.name),
        };
        return html`
            <button
                class="btn btn-toggle flex-col justify-center ${classMap(btnClasses)}"
                data-click
                data-name="${value.name}"
                data-index="${index}"
            >
                <div>${value.label}</div>
                ${priceDifference
                    ? html`
                          <div class="font-thin text-sm text-grey-400">
                              ${CurrencyFormatter.formattedPriceWithSign(priceDifference)}
                          </div>
                      `
                    : ''}
            </button>
        `;
    }

    colorButtonTemplate(value, index) {
        return html`
            <color-button-toggle
                data-click
                data-name="${value.name}"
                data-label="${value.label}"
                data-index="${index}"
                class="col w-3/12"
                active="${this.isSelected(value.name)}"
                colors="${JSON.stringify(value.colors)}"
                disabled="${!this.isAvailable(value.name)}"
                render-disabled-attribute="false"
            ></color-button-toggle>
        `;
    }

    tooltipTemplate(value) {
        const tooltipLabel = this.getTooltipLabelByAvailability(value.name);
        return tooltipLabel
            ? html`
                  <arrow-box class="tooltip font-medium bg-grey-800 text-white p-2 text-sm whitespace-nowrap">
                      ${tooltipLabel}
                  </arrow-box>
              `
            : '';
    }
}

defineElement('buy-box-spec', BuyBoxSpec);
